import React, { useState } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { cnpjMask, validaCNPJ } from '../../utils';
import { createAdvertiser, updateAdvertiser } from '../../api/advertiserApi';
import { Advertiser } from '../../types/advertiser';

interface AddModalProps {
    advertiser: Advertiser | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Advertiser) => void;
    onUpdateData: (newData: Advertiser) => void;
}

const AddEditAdvertiserModal = ({ advertiser, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const toggleAddModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            name: '',
            cnpj: '',
            // users: [],
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .trim()
                .required('Nome é obrigatório'),
            cnpj: Yup.string()
                .nullable()
                .optional()
                .test('is-valid-cnpj', 'CNPJ inválido', (value: any) => value ? validaCNPJ(value) : true),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            if (advertiser) {
                updateAdvertiser(values)
                    .then((res: any) => {
                        toggleAddModal();
                        onUpdateData(res);
                        toast.success('Anunciante atualizado com sucesso!');
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAdvertiser(values)
                    .then((res: any) => {
                        toggleAddModal();
                        onAddData(res);
                        toast.success('Anunciante cadastrado com sucesso!');
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
               backdrop={false} centered>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    Adicionar anunciante
                </ModalHeader>

                <ModalBody>
                    <div className="mb-3">
                        <Label
                            htmlFor="name"
                            className="form-label"
                        >
                            Nome
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                            invalid={
                                !!(handleForm.touched.name && handleForm.errors.name)
                            }
                        />
                        {handleForm.touched.name && handleForm.errors.name ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.name}</FormFeedback>
                        ) : null}
                    </div>

                    <div className="mb-3">
                        <Label
                            htmlFor="cnpj"
                            className="form-label"
                        >
                            CNPJ
                        </Label>
                        <Input
                            className="form-control"
                            type="text"
                            id="cnpj"
                            name="cnpj"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={cnpjMask(handleForm.values.cnpj)}
                            invalid={
                                !!(handleForm.touched.cnpj && handleForm.errors.cnpj)
                            }
                        />
                        {handleForm.touched.cnpj && handleForm.errors.cnpj ? (
                            <FormFeedback
                                type="invalid">{handleForm.errors.cnpj}</FormFeedback>
                        ) : null}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        Cadastrar
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditAdvertiserModal;